/**
 * App-wide providers: React Query + Sonner toast portal.
 *
 * Auth state is managed via `useSession` which subscribes to Supabase
 * inside its own hook — there's no separate AuthProvider to mount here.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Toaster } from "sonner";

export function AppProviders({ children }: { children: ReactNode }) {
  // useState so the client survives strict-mode double mount; a top-level
  // `const qc = new QueryClient()` would rebuild the cache on hot reload.
  const [qc] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Sensible default: don't retry 4xx (user/perm errors); do
            // retry transient 5xx/network errors a couple of times.
            retry: (failureCount, error: unknown) => {
              const status = (error as { status?: number })?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 2;
            },
            refetchOnWindowFocus: true,
            staleTime: 30_000,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={qc}>
      {children}
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}
